import type {
  IResponseApi,
  IResponseApiSearch,
  IRequestConfig,
  ServiceOptions
} from '@/modules/Core/interfaces/api/IApis'
import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type AxiosError
} from 'axios'
import { type VueCookies } from 'vue-cookies'
import type { Router } from 'vue-router'
import { reportarError } from '@/modules/Core/errores/errorReporter'
export class BaseService<T> {
  protected http: AxiosInstance
  protected baseUrl: string
  static router: Router
  static cookies: VueCookies
  private options: ServiceOptions
  constructor(options: { baseUrl: string }) {
    this.baseUrl = options.baseUrl
    this.options = options
    this.http = axios.create({
      baseURL: `${import.meta.env.VITE_API_URL}/api/v1/${this.baseUrl}`
    })
    this.http.interceptors.response.use(
      (response) => {
        if (response.data?.warning) {
          window.notificacion.report('warning', 'Aviso', response.data.body)
        }
        return response
      }
    )
    this.http.interceptors.request.use(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (config: any) => {
        const token = BaseService.cookies.get('token') // Obtén el token de las cookies
        if (config.headers && token) {
          config.headers['token'] = token
        }
        return config
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error: any) => {
        return Promise.reject(error)
      }
    )
  }
  static configureDependencies(
    router: Router,
    cookies: VueCookies
  ) {
    BaseService.router = router
    BaseService.cookies = cookies!
  }
  /**
   * Manejo centralizado de errores HTTP
   *
   * @param error - Error de Axios que contiene la respuesta HTTP
   *
   * Códigos de estado HTTP manejados:
   *
   * - 400 (Bad Request): Usado internamente para indicar sesión caducada.
   *   Limpia cookies y redirige al login.
   *
   * - 401 (Unauthorized): Credenciales inválidas o token expirado.
   *   Muestra mensaje de error de autenticación.
   *
   * - 403 (Forbidden): Usuario no tiene permisos para la acción solicitada.
   *   Muestra mensaje de falta de permisos.
   *
   * - 422 (Unprocessable Entity): Error de validación de datos.
   *   Extrae y muestra mensajes específicos del servidor o errores por campo.
   *   Prioriza: data.message > data.body > data.errors > mensaje genérico.
   *
   * - 500 (Internal Server Error): Error interno del servidor.
   *   Muestra mensaje técnico y solicita contactar soporte.
   *
   * - 502 (Bad Gateway): Usado para operaciones no permitidas.
   *   Indica que el registro no se puede modificar debido a restricciones del sistema.
   *
   * - Otros errores: Cualquier otro código de estado no contemplado.
   *   Muestra mensaje genérico de error.
   *
   * Errores sin respuesta HTTP:
   * - Errores de red, timeout, o problemas de código.
   *   Muestra mensaje genérico de error de conexión.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errorHandle = (error: AxiosError): any => {
    const { cookies } = BaseService
    // El llamador puede pedir manejar el error por su cuenta.
    const notificar = (error.config as { notificarError?: boolean } | undefined)?.notificarError !== false
    if (error.response) {
      // Los errores del servidor (5xx) y los inesperados se reportan al sink
      // central. NO se loguea error.response.data crudo: puede traer PII de
      // pacientes (Habeas Data). El reporter sanitiza y enruta.
      if (error.response.status >= 500) {
        reportarError(error, {
          origen: 'http',
          extra: {
            status: error.response.status,
            url: error.config?.url,
            metodo: error.config?.method
          }
        })
      }
      if (error.response.status == 401) {
        window.notificacion.report(
          'warning',
          'Credenciales invalidas',
          'Por favor verificar sus credenciales.'
        )
      } else if (error.response.status == 423) {
        window.notificacion.report('warning', 'Aviso de licencia', (error.response.data as IResponseApi<string>).body)
        if (cookies.get('token') && error.config?.url !== '/change-entity') window.location.reload()
      } else if (error.response.status == 503) {
        window.notificacion.report('warning', 'Estamos fuera de servicio!', 'Ventana de mantenimiento.')
        window.location.href = '/error503'
      }
      else if (error.response.status == 403) {
        // window.location.href = '/error403'
        window.notificacion.report('warning', 'No autorizado!', 'No tiene permisos para realizar esta acción')
      } else if (error.response.status == 400) {
        cookies.keys().forEach((cookie: string) => cookies.remove(cookie))
        window.notificacion.report(
          'warning',
          'Sesion caducada!',
          'Inicie sesion nuevamente'
        )
        window.location.href = '/login'
      } else if (error.response.status == 422) {
        // Error de validación
        if (
          typeof error.response.data === 'object' &&
          error.response.data !== null
        ) {
          const data = error.response.data as { [key: string]: unknown }

          // Intentar extraer mensaje específico del servidor
          let mensaje = 'Por favor revise los campos marcados e intente nuevamente'

          if (data.message && typeof data.message === 'string') {
            mensaje = data.message
          } else if (data.body && typeof data.body === 'string') {
            mensaje = data.body
          } else if (data.errors && typeof data.errors === 'object') {
            // Si hay errores específicos por campo, mostrar el primer error
            const primerError = Object.values(data.errors as Record<string, unknown>)[0]
            if (Array.isArray(primerError) && primerError.length > 0) {
              mensaje = String(primerError[0])
            } else if (typeof primerError === 'string') {
              mensaje = primerError
            }
          }

          if (notificar) {
            window.notificacion.report(
              'warning',
              'Error de validación',
              mensaje
            )
          }
        } else if (notificar) {
          // Error 422 sin datos específicos
          window.notificacion.report(
            'warning',
            'Error de validación',
            'Los datos ingresados no son válidos. Por favor verifique e intente nuevamente.'
          )
        }
      } else if (error.response.status == 502) {
        const data = error.response.data as { [key: string]: string }
        if (notificar) {
          window.notificacion.report(
            'warning',
            'Operación no permitida',
            data.body || 'No se puede modificar este registro debido a restricciones del sistema.'
          )
        }
      } else if (error.response.status == 500) {
        if (
          typeof error.response.data === 'object' &&
          error.response.data !== null
        ) {
          const data = error.response.data as { [key: string]: string }
          window.notificacion.report('failure', 'Contactar con soporte.', data.body || 'Ha ocurrido un error interno. Contacte con soporte técnico.')
        }
      } else {
        window.notificacion.report('failure', 'Algo sucedio!', 'contacta con soporte')
      }
      throw error
    } else if (error) {
      if (error.status == 503) {
        window.notificacion.report('warning', 'Estamos fuera de servicio!', 'Ventana de mantenimiento.')
        window.location.href = '/error503'
      }
      reportarError(error, { origen: 'http', extra: { tipo: 'sin-respuesta' } })
      window.notificacion.report('failure', 'Algo sucedio!', 'contacta con soporte')
      throw error
    }
  }
  // Listar todos los elementos
  async list(
    params?: Record<string, unknown>
  ): Promise<AxiosResponse<IResponseApi<T[]>>> {
    return this.http.get<IResponseApi<T[]>>(`/list`, { params })
  }

  // Buscar elementos con parámetros
  async search(
    params: Record<string, unknown>
  ): Promise<AxiosResponse<IResponseApiSearch<T[]>>> {
    return this.http
      .get<IResponseApiSearch<T[]>>(`/search`, { params })
      .catch(this.errorHandle)
  }

  // Ver un elemento por ID
  async view(
    id: number | string | string[],
    params?: Record<string, unknown>
  ): Promise<AxiosResponse<IResponseApi<T>>> {
    return this.http.get<T>(`/view/${id}`, { params }).catch(this.errorHandle)
  }

  // Crear un nuevo elemento
  async create(
    data: Record<string, unknown> | Record<string, unknown>[] | T | T[] | FormData,
    config?: IRequestConfig
  ): Promise<AxiosResponse<IResponseApi<T | T[]>>> {
    return this.http.post<T | T[]>(`/create`, data, config).catch(this.errorHandle)
  }

  // Actualizar un elemento por ID
  async update(
    id: number | string,
    data: Record<string, unknown> | Record<string, unknown>[] | Partial<T> | Partial<T[]> | FormData,
    config?: IRequestConfig
  ): Promise<AxiosResponse<IResponseApi<T>>> {
    return this.http.put<T>(`/update/${id}`, data, config).catch(this.errorHandle)
  }

  // Eliminar un elemento por ID
  async delete(
    id: number | string
  ): Promise<AxiosResponse<IResponseApi<void>>> {
    return this.http.delete<void>(`/delete/${id}`).catch(this.errorHandle)
  }

  async postFile(
    url: string,
    data: FormData
  ): Promise<AxiosResponse<IResponseApi<T>>> {
    return this.http
      .post<T>(url, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .catch(this.errorHandle)
  }

  // Cargar un archivo con PUT
  async putFile(
    url: string,
    data: FormData
  ): Promise<AxiosResponse<IResponseApi<T>>> {
    return this.http
      .put<T>(url, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .catch(this.errorHandle)
  }

  // Descargar un archivo con responseType arraybuffer
  async getFile(
    url: string
  ): Promise<AxiosResponse<IResponseApi<ArrayBuffer>>> {
    return this.http
      .get<ArrayBuffer>(url, {
        responseType: 'arraybuffer',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Max-Age': '0'
        }
      })
      .catch(this.errorHandle)
  }
  async gzipString(str: string) {
    const cs = new CompressionStream("gzip");
    const writer = cs.writable.getWriter();

    writer.write(new TextEncoder().encode(str));
    writer.close();

    const compressed = await new Response(cs.readable).arrayBuffer();
    return new Uint8Array(compressed);
  }
}
